import { useMemo } from "react"

import { ResearchProjectResult } from "interface/research"

import { GridColumnDefMap } from "components/grid/types"
import { columnBase, formatter } from "components/grid/util"
import { renderer } from "components/grid/renderer"

type useResearchGridColumnsProps = {
	onClickLink: (projectKey: string) => void
}
export const useResearchGridColumns = ({
	onClickLink
}: useResearchGridColumnsProps) => {
	const columnsDef = useMemo<GridColumnDefMap<ResearchProjectResult>>(
		() => {
			const columnsDef: GridColumnDefMap<ResearchProjectResult> = {
				'link': {
					headerName: '詳細',
					hideable: false,
					sticky: true,
					value: (r) => r.projectKey,
					...columnBase.action.openByValue(onClickLink),
				},
				'kadaiNo': {
					headerName: '課題番号',
					width: 138,
					value: (r) => r.researchs[0]?.kadaiNo ?? `単独-${r.projectKey}`
				},
				'kubunName': {
					headerName: '研究区分',
					value: (r) => r.researchs[0]?.kubunName,
					filterType: 'candidates'
				},
				'bunyaName': {
					headerName: '研究分野',
					value: (r) => r.researchs[0]?.bunyaName,
					filterType: 'candidates'
				},
				'jigyouName': {
					headerName: '事業名',
					value: (r) => r.researchs[0]?.jigyouName,
					filterType: 'candidates'
				},
				'startNendo': {
					headerName: '開始年度',
					value: (r) => r.startNendo,
					valueFormatter: formatter.year,
					filterType: 'histgram'
				},
				'endNendo': {
					headerName: '終了年度',
					value: (r) => r.endNendo,
					valueFormatter: formatter.year,
					filterType: 'histgram'
				},
				'title': {
					headerName: '課題名',
					width: 500,
					sticky: true,
					value: (r) => r.projectName,
					filterType: 'text'
				},
				'reportType': {
					headerName: '報告書区分',
					value: (r) => r.researchs[0]?.reportType,
					filterType: 'candidates'
				},
				'primaryResearcher': {
					headerName: '研究代表者',
					width: 200,
					value: (r) => r.researchs[0]?.primaryResearcher?.kojinName,
					filterType: 'candidates'
				},
				'primaryResearcherSisetu': {
					headerName: '研究代表者施設',
					width: 200,
					value: (r) => r.researchs[0]?.primaryResearcher?.facility?.sisetuName,
					filterType: 'candidates'
				},
				'supportResearchers': {
					headerName: '研究分担者名',
					width: 200,
					value: (r) => r.researchs[0]?.supportResearchers?.map(researcher => researcher.kojinName),
					renderCell: (params) => renderer.multiRow(params.value, 'vertical', 5),
					filterType: 'candidates'
				},
				'researchCost': {
					headerName: '研究費',
					width: 100,
					value: (r) => r.totalCost,
					valueFormatter: formatter.currency,
					align: 'right',
					filterType: 'histgram',
					histgram: {
						unit: 1000000
					}
				},
				'purpose': {
					headerName: '研究目的',
					defaultShow: false,
					width: 500,
					value: (r) => r.researchs[0]?.purpose,
					renderCell: (params) => renderer.clampText(params.value, 5),
					filterType: 'sentense'
				},
				'method': {
					headerName: '研究方法',
					defaultShow: false,
					width: 500,
					value: (r) => r.researchs[0]?.method,
					renderCell: (params) => renderer.clampText(params.value, 5),
					filterType: 'sentense'
				},
				'results': {
					headerName: '結果と考察',
					defaultShow: false,
					width: 500,
					value: (r) => r.researchs[0]?.results,
					renderCell: (params) => renderer.clampText(params.value, 5),
					filterType: 'sentense'
				},
				'conclusion': {
					headerName: '結論',
					defaultShow: false,
					width: 500,
					value: (r) => r.researchs[0]?.conclusion,
					renderCell: (params) => renderer.clampText(params.value, 5),
					filterType: 'sentense'
				},
				'abstract': {
					headerName: '概要',
					defaultShow: false,
					width: 500,
					value: (r) => r.researchs[0]?.abstract,
					renderCell: (params) => renderer.clampText(params.value, 5),
					filterType: 'sentense'
				},
				'words': {
					headerName: 'キーワード',
					defaultShow: false,
					width: 200,
					value: (r) => r.researchs[0]?.words,
					renderCell: (params) => renderer.multiRow(params.value, 'horizon'),
					filterType: 'candidates'
				}
			}
			return columnsDef
		},
		[onClickLink]
	)
	return { columnsDef }
}